/**
 * O ROMANEIO — a conta que viaja para o comprador.
 *
 * É o único papel deste sistema que sai da fazenda. Por isso a conta mora aqui,
 * fora da tela, com teste: um erro no romaneio não fica dentro de casa.
 *
 * ── A CONTA, tal como o gestor a descreveu ────────────────────────────────
 *
 *   por espécie:  soma dos pesos  −  desconto de umidade  ×  preço por quilo
 *
 * Duas assimetrias, e as duas são decisão da fazenda, não detalhe:
 *
 *   O DESCONTO É UM SÓ para a despesca inteira. Entre 3% e 5%, quase sempre 3%,
 *   e pergunta-se uma vez. É abatimento comercial, não propriedade do peixe.
 *
 *   O PREÇO É POR ESPÉCIE. «Mil quilos de tambaqui e dez de piau são dois
 *   preços» — e uma média entre eles não descreveria nenhuma das duas vendas.
 *
 * ── O QUE O DESCONTO NÃO PODE TOCAR ───────────────────────────────────────
 *
 * O peso que vai para o BANCO é o pesado, não o cobrado. O desconto é um
 * acerto de dinheiro entre duas pessoas; o peixe que saiu da represa saiu
 * inteiro. Misturar os dois faria a conversão alimentar da fazenda melhorar 3%
 * por decisão comercial, e a curva de crescimento junto.
 *
 * Por isso o romaneio devolve os dois pesos separados, sempre, e quem grava
 * escolhe qual é qual.
 *
 * ── O DOCUMENTO TEM DE FECHAR NA MÃO DE QUEM CONFERE ──────────────────────
 *
 * Toda linha é arredondada ao CENTÉSIMO DE QUILO (10 g) e ao CENTAVO, e o
 * total é a soma das linhas já arredondadas — nunca o arredondamento da soma.
 *
 * A diferença não é acadêmica. Com uma casa decimal e 3% de desconto, uma
 * despesca de 2,5 + 3,1 + 1,7 kg imprime «0,1 + 0,1 + 0,1» nas linhas e «0,2»
 * no total: quem somar a lápis encontra 0,3 e conclui que a fazenda errou a
 * conta. Num papel sobre dinheiro, número que não fecha não é imprecisão — é
 * motivo de desconfiança, e a conversa seguinte não é sobre aritmética.
 *
 * Dez gramas é mais fino que a balança da despesca consegue ler, então o
 * arredondamento não perde medição nenhuma: ele só impede que a soma das
 * partes discorde do todo.
 */

import { apurarBloco, type Bloco } from './pesagem'
import { numero, numeroNaFaixa } from './unidades'

/**
 * Ao centésimo: 10 g no peso, 1 centavo no dinheiro.
 *
 * Mais fino que a balança da despesca lê, e é a menor unidade em que quem
 * confere a lápis consegue fechar a conta.
 */
export function aoCentesimo(v: number): number {
  return Math.round(v * 100) / 100
}

/** Quanto o comprador pode abater, em %. Acima disso não é umidade. */
export const MAX_DESCONTO_PCT = 20

/** O teto do preço por quilo. Existe para pegar o dedo pesado, não para julgar. */
export const MAX_PRECO_KG = 1000

export interface LinhaDoRomaneio {
  especie: string
  peixes: number
  /** O que a balança marcou, já sem a tara. É este que vai para o banco. */
  pesadoKg: number
  /** O abatimento comercial, em quilos. */
  descontoKg: number
  /** O que o comprador paga. É este que multiplica pelo preço. */
  cobradoKg: number
  mediaG: number
  precoKg: number | null
  valorBrl: number | null
}

export interface Romaneio {
  linhas: LinhaDoRomaneio[]
  peixes: number
  pesadoKg: number
  descontoKg: number
  cobradoKg: number
  mediaG: number
  /** Nulo enquanto qualquer espécie estiver sem preço: total pela metade mente. */
  valorBrl: number | null
  descontoPct: number
}

/** A ordem é a da primeira aparição na rede, não a alfabética. */
function especieDoBloco(b: Bloco): string {
  const nome = (b.especie ?? '').trim()
  return nome === '' ? 'tambaqui' : nome
}

/**
 * Os cestões somados por espécie.
 *
 * Quarenta cestões viram três linhas, uma por espécie — e é assim que vão para
 * o banco. Linha por cestão faria quarenta registros dizendo a mesma coisa, e
 * qualquer conta posterior teria de somá-los de volta.
 */
export function porEspecie(blocos: Bloco[], comTara: boolean): LinhaDoRomaneio[] {
  const ordem: string[] = []
  const somas = new Map<string, { peixes: number; pesadoKg: number }>()

  for (const b of blocos) {
    const a = apurarBloco(b, comTara)
    if (!a) continue
    const especie = especieDoBloco(b)
    const atual = somas.get(especie)
    if (atual) {
      atual.peixes += a.peixes
      atual.pesadoKg += a.liquidoKg
    } else {
      ordem.push(especie)
      somas.set(especie, { peixes: a.peixes, pesadoKg: a.liquidoKg })
    }
  }

  return ordem.map((especie) => {
    const s = somas.get(especie) ?? { peixes: 0, pesadoKg: 0 }
    // O arredondamento acontece AQUI, uma vez, e tudo que vem depois parte
    // deste número — inclusive o que vai para o banco. Assim o papel, a tela,
    // a planilha e o histórico dizem todos o mesmo peso.
    const pesadoKg = aoCentesimo(s.pesadoKg)
    return {
      especie,
      peixes: s.peixes,
      pesadoKg,
      descontoKg: 0,
      cobradoKg: pesadoKg,
      mediaG: (pesadoKg * 1000) / s.peixes,
      precoKg: null,
      valorBrl: null,
    }
  })
}

/**
 * O romaneio inteiro: espécies somadas, desconto aplicado, preços casados.
 *
 * `descontoTexto` e os preços chegam como TEXTO de campo de propósito — a
 * leitura do padrão brasileiro (vírgula decimal, ponto de milhar) acontece
 * aqui, num lugar só, e não espalhada pela tela.
 */
export function montarRomaneio(
  blocos: Bloco[],
  comTara: boolean,
  descontoTexto: string,
  precos: Record<string, string>,
): Romaneio | null {
  const base = porEspecie(blocos, comTara)
  if (base.length === 0) return null

  // Desconto em branco é desconto zero, e isso é legítimo: há venda sem
  // abatimento. O que não é legítimo é texto ilegível virar zero calado — por
  // isso quem digitou algo impossível recebe o romaneio nulo, e a tela avisa.
  const descontoPct =
    descontoTexto.trim() === '' ? 0 : numeroNaFaixa(descontoTexto, 0, MAX_DESCONTO_PCT)
  if (descontoPct === null) return null
  return fecharRomaneio(base, descontoPct, (l) => precoDigitado(precos[l.especie] ?? ''))
}

/**
 * O PREÇO DIGITADO, COM NO MÁXIMO DUAS CASAS — o banco guarda centavos
 * (`numeric(10,2)`). «11,555» fecharia o papel a 11,555 e o banco guardaria
 * 11,56: o romaneio reaberto daria outro valor para a mesma venda. Recusado
 * aqui, a tela pede o preço de novo em vez de imprimir um número que não fecha.
 */
export function precoDigitado(texto: string): number | null {
  if (texto.trim() === '') return null
  const decimais = texto.includes(',') ? (texto.split(',')[1] ?? '').trim().length : 0
  if (decimais > 2) return null
  return numeroNaFaixa(texto, 0, MAX_PRECO_KG)
}

/** O preço foi digitado, mas não vale (três casas, letra, fora da faixa). */
export function precoInvalido(texto: string): boolean {
  return texto.trim() !== '' && precoDigitado(texto) === null
}

/**
 * O FECHAMENTO DO ROMANEIO — desconto e preço sobre as espécies já somadas.
 *
 * Uma conta só para os dois caminhos: o romaneio montado na despesca, a partir
 * dos cestões, e o romaneio REABERTO depois, a partir do que ficou no banco.
 * Duas contas parecidas dariam, um dia, dois papéis diferentes para a mesma
 * venda — e o segundo iria para o comprador.
 */
function fecharRomaneio(
  base: LinhaDoRomaneio[],
  descontoPct: number,
  precoDe: (linha: LinhaDoRomaneio) => number | null,
): Romaneio {
  const fracao = descontoPct / 100

  let peixes = 0
  let pesadoKg = 0
  const descontos: number[] = []
  let cobradoKg = 0
  let valorBrl = 0
  let faltaPreco = false

  const linhas = base.map((l) => {
    // Cada linha fecha em si mesma: o líquido é a subtração exata dos dois
    // números impressos, e não uma terceira conta que por acaso se parece.
    const descontoKg = aoCentesimo(l.pesadoKg * fracao)
    const cobrado = aoCentesimo(l.pesadoKg - descontoKg)
    const precoKg = precoDe(l)
    if (precoKg === null) faltaPreco = true
    const valor = precoKg === null ? null : aoCentesimo(cobrado * precoKg)

    peixes += l.peixes
    pesadoKg += l.pesadoKg
    descontos.push(descontoKg)
    cobradoKg += cobrado
    if (valor !== null) valorBrl += valor

    return { ...l, descontoKg, cobradoKg: cobrado, precoKg, valorBrl: valor }
  })

  // OS TOTAIS SÃO A SOMA DAS LINHAS ARREDONDADAS, nunca o arredondamento da
  // soma. É o que garante que quem conferir a lápis chegue ao mesmo número.
  return {
    linhas,
    peixes,
    pesadoKg: aoCentesimo(pesadoKg),
    descontoKg: aoCentesimo(descontos.reduce((t, x) => t + x, 0)),
    cobradoKg: aoCentesimo(cobradoKg),
    mediaG: (aoCentesimo(pesadoKg) * 1000) / peixes,
    // Total pela metade mente mais do que total ausente: quem vê «R$ 12.400»
    // sem reparar que falta o preço do piau leva o número errado ao comprador.
    valorBrl: faltaPreco ? null : aoCentesimo(valorBrl),
    descontoPct,
  }
}

/** Uma linha de despesca como ficou no banco — uma por espécie. */
export interface LinhaGravada {
  especie: string | null
  quantidade: number
  peso_total_kg: number
  preco_kg_brl: number | null
}

/**
 * O ROMANEIO DE UMA DESPESCA JÁ GRAVADA — para reabrir e reenviar.
 *
 * Pedido do gestor em 06/10/2026: despescou o T-2, gravou, e o papel sumiu
 * junto com o formulário — não havia onde achá-lo para mandar ao comprador.
 *
 * O banco guarda o PESADO de cada espécie (já arredondado ao centésimo na
 * despesca), o preço, e o desconto como fração (0,03). Daí sai o mesmo papel,
 * pela mesma conta (`fecharRomaneio`) — e o teste confere centavo por centavo
 * que o reaberto é igual ao original.
 *
 * A ordem das espécies é a do peso, maior primeiro: a ordem da rede não foi
 * guardada, e a espécie principal na primeira linha é o que se procura.
 */
export function romaneioGravado(
  linhas: LinhaGravada[],
  descontoFracao: number | null,
): Romaneio | null {
  const base: LinhaDoRomaneio[] = linhas
    .filter((l) => l.quantidade > 0 && l.peso_total_kg > 0)
    .map((l) => {
      const pesadoKg = aoCentesimo(l.peso_total_kg)
      return {
        especie: (l.especie ?? '').trim() === '' ? 'tambaqui' : (l.especie as string),
        peixes: l.quantidade,
        pesadoKg,
        descontoKg: 0,
        cobradoKg: pesadoKg,
        mediaG: (pesadoKg * 1000) / l.quantidade,
        precoKg: l.preco_kg_brl,
        valorBrl: null,
      }
    })
    .sort((a, b) => b.pesadoKg - a.pesadoKg)
  if (base.length === 0) return null
  // 0,03 vira 3 — e não 3,0000000000000004, que é o que a conta crua daria.
  // Seis casas, e não duas: um 2,125% digitado tem de voltar 2,125%.
  const descontoPct = Math.round((descontoFracao ?? 0) * 100 * 1e6) / 1e6
  // O preço de CADA LINHA, e não um mapa por espécie: duas linhas da mesma
  // espécie, se um dia houver, não podem trocar de preço entre si.
  return fecharRomaneio(base, descontoPct, (l) => l.precoKg)
}

/** «R$ 12.480,50» — o jeito de escrever dinheiro que o comprador espera. */
export function emReais(v: number): string {
  return `R$ ${numero(v, 2)}`
}
